import React from 'react';
import classNames from 'classnames';
import sortBy from 'lodash/sortBy';
import {CSSTransition, TransitionGroup} from 'react-transition-group';
import {Button, TextStyle, Icon} from '@shopify/polaris';
import {TaxonomyNode} from 'sections/types';

import styles from './ServiceSelector.scss';

export const SLIDE = {
  LEFT: 'slide-left',
  RIGHT: 'slide-right',
};
const initialHeight = 'auto';
const initialMaxHeight = 'none';
const transitionTimeout = parseInt(styles.transitionTimeout, 10);

interface OwnProps {
  taxonomy: TaxonomyNode[];
  activeTaxonomyNode: TaxonomyNode[];
  breadcrumbs: TaxonomyNode[] | [];
  onServiceSelect: (
    serviceHandle: string,
    activeTaxonomyNode: TaxonomyNode[],
    breadcrumbs: TaxonomyNode[]
  ) => void;
}

interface State {
  breadcrumbs: TaxonomyNode[] | [];
  activeTaxonomyNode: TaxonomyNode[];
  slideDirection: string;
  isAnimating: boolean;
  height: string | number;
  maxHeight: string | number;
  currentHeight: string | number;
}

type OnSelect = (node: TaxonomyNode) => (evt: React.MouseEvent) => void;

export class ServiceSelector extends React.Component<OwnProps, State> {
  state: State = {
    breadcrumbs: this.props.breadcrumbs,
    activeTaxonomyNode: this.props.activeTaxonomyNode,
    slideDirection: SLIDE.LEFT,
    isAnimating: false,
    height: initialHeight,
    maxHeight: initialMaxHeight,
    currentHeight: 'auto',
  };

  private focusRef: React.RefObject<HTMLUListElement> = React.createRef();

  setPreviousStateFromStore(
    breadcrumbs: TaxonomyNode[],
    activeTaxonomyNode: TaxonomyNode[]
  ) {
    this.setState({
      breadcrumbs,
      activeTaxonomyNode,
    });
  }

  setListWrapperHeight() {
    const listWrapper = document.querySelector<HTMLElement>(
      `.${styles.ListWrapper}`
    );

    if (!listWrapper) {
      return;
    }

    this.setState({
      currentHeight: listWrapper.offsetHeight,
    });
  }

  onGroupSelect = (node: TaxonomyNode) => {
    const {currentHeight, breadcrumbs} = this.state;

    return () => {
      this.setState({
        breadcrumbs: [...breadcrumbs, node],
        activeTaxonomyNode: sortBy(node.children, ['children']),
        slideDirection: SLIDE.LEFT,
        height: currentHeight,
        maxHeight: currentHeight,
      });
    };
  };

  onBreadcrumbSelect = (breadcrumbIndex: number) => {
    const {taxonomy} = this.props;
    const {breadcrumbs, currentHeight} = this.state;
    const activeTaxonomyNode =
      breadcrumbIndex === 0
        ? taxonomy
        : sortBy(breadcrumbs[breadcrumbIndex - 1].children, ['children']);

    return () => {
      this.setState({
        breadcrumbs: [...breadcrumbs.slice(0, breadcrumbIndex)],
        activeTaxonomyNode,
        slideDirection: SLIDE.RIGHT,
        height: currentHeight,
        maxHeight: currentHeight,
      });
    };
  };

  onServiceSelect = (node: TaxonomyNode) => {
    const {onServiceSelect} = this.props;
    const {activeTaxonomyNode, breadcrumbs} = this.state;
    const {
      service: {handle},
    } = node;

    return () => {
      onServiceSelect(handle, activeTaxonomyNode, breadcrumbs);
    };
  };

  transitionFactoryCreator(classNames: string) {
    return (child: React.FunctionComponentElement<{classNames: string}>) =>
      React.cloneElement(child, {classNames});
  }

  onSlideEnter = (node: HTMLElement) => {
    const currentHeight = node.offsetHeight;

    this.setState({
      isAnimating: true,
      height: currentHeight,
      maxHeight: currentHeight,
      currentHeight,
    });

    this.setFocus();
  };

  onSlideExit = () => {
    this.setState({
      isAnimating: false,
      height: initialHeight,
      maxHeight: initialMaxHeight,
    });
  };

  setFocus = () => {
    this.focusRef.current && this.focusRef.current.focus();
  };

  componentDidMount() {
    this.setListWrapperHeight();
  }

  componentDidUpdate(prevProps: OwnProps) {
    if (prevProps.taxonomy !== this.props.taxonomy) {
      this.setListWrapperHeight();
      this.setPreviousStateFromStore(
        this.props.breadcrumbs,
        this.props.activeTaxonomyNode
      );
    }
  }

  render() {
    const {activeTaxonomyNode, slideDirection, breadcrumbs} = this.state;
    const currentBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
    const transitionKey = currentBreadcrumb
      ? currentBreadcrumb.service.handle
      : 'root';

    return (
      <div className={styles.Container}>
        <div>
          <Breadcrumbs
            breadcrumbs={breadcrumbs}
            onBreadcrumbSelect={this.onBreadcrumbSelect}
          />
          <div
            className={styles.HeightTransitioner}
            style={{
              height: this.state.height,
              maxHeight: this.state.maxHeight,
            }}
          >
            <TransitionGroup
              className={classNames(styles.AnimateWrapper, {
                [styles.IsAnimating]: this.state.isAnimating,
              })}
              childFactory={this.transitionFactoryCreator(slideDirection)}
            >
              <CSSTransition
                key={transitionKey}
                classNames={slideDirection}
                timeout={transitionTimeout}
                onEnter={this.onSlideEnter}
                onExited={this.onSlideExit}
              >
                <ServiceList
                  services={activeTaxonomyNode}
                  onGroupSelect={this.onGroupSelect}
                  onServiceSelect={this.onServiceSelect}
                  focusRef={this.focusRef}
                />
              </CSSTransition>
            </TransitionGroup>
          </div>
        </div>
      </div>
    );
  }
}

interface ServiceList {
  services: TaxonomyNode[];
  onGroupSelect: OnSelect;
  onServiceSelect: OnSelect;
  focusRef: React.RefObject<HTMLUListElement>;
}

function ServiceList({
  services,
  onGroupSelect,
  onServiceSelect,
  focusRef,
}: ServiceList) {
  return (
    <div className={styles.ListWrapper}>
      <ul
        className={styles.List}
        tabIndex={-1}
        ref={focusRef}
        aria-live="polite"
      >
        {services.map(node => {
          return node.service.hidden === true ? null : (
            <li className={styles.ListItem} key={node.service.handle}>
              {node.children && node.children.length ? (
                <GroupItem node={node} onGroupSelect={onGroupSelect} />
              ) : (
                <ServiceItem node={node} onServiceSelect={onServiceSelect} />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface GroupItem {
  node: TaxonomyNode;
  onGroupSelect: OnSelect;
}

function GroupItem({node, onGroupSelect}: GroupItem) {
  const {service} = node;
  const className = classNames(styles.ListItemButton, styles.ListItemGroup);

  return (
    <button className={className} onClick={onGroupSelect(node)} type="button">
      <span className={styles.ServiceLabel}>
        <TextStyle variation="strong">{service.label} </TextStyle>
        <span className={styles.ServiceDescription}>
          <TextStyle variation="subdued">{service.description}</TextStyle>
        </span>
      </span>
      <span className={styles.ArrowIcon}>
        <Icon source="chevronRight" color="inkLightest" />
      </span>
    </button>
  );
}

interface ServiceItem {
  node: TaxonomyNode;
  onServiceSelect: OnSelect;
}

function ServiceItem({node, onServiceSelect}: ServiceItem) {
  const {service} = node;

  return (
    <button
      className={styles.ListItemButton}
      onClick={onServiceSelect(node)}
      type="button"
    >
      <span className={styles.ServiceLabel}>
        <TextStyle variation="strong">{service.label} </TextStyle>
        <span className={styles.ServiceDescription}>
          <TextStyle variation="subdued">{service.description}</TextStyle>
        </span>
      </span>
      <span className={styles.ButtonStyle}>Select</span>
    </button>
  );
}

interface Breadcrumbs {
  breadcrumbs: TaxonomyNode[];
  onBreadcrumbSelect(index: number): () => void;
}

function Breadcrumbs({breadcrumbs, onBreadcrumbSelect}: Breadcrumbs) {
  const className = classNames(
    styles.Breadcrumbs,
    breadcrumbs.length === 0 && styles.EmptyBreadcrumbs
  );

  return (
    <div className={className}>
      <div className={styles.BreadcrumbButton}>
        <span className={styles.HideDesktop}>
          <Icon source="chevronRight" color="inkLighter" />{' '}
        </span>
        <Button plain onClick={onBreadcrumbSelect(0)}>
          All services
        </Button>
      </div>

      {breadcrumbs.map((node, idx) =>
        idx + 1 === breadcrumbs.length ? (
          <span key={node.service.handle} className={styles.ActiveBreadcrumb}>
            <Icon source="chevronRight" color="inkLighter" />{' '}
            {node.service.shortLabel}
          </span>
        ) : (
          <div key={node.service.handle} className={styles.BreadcrumbButton}>
            <span>
              <Icon source="chevronRight" color="inkLighter" />{' '}
            </span>
            <Button onClick={onBreadcrumbSelect(idx + 1)} plain>
              {node.service.shortLabel}
            </Button>
          </div>
        )
      )}
    </div>
  );
}
